import { EmailTemplateData } from "@labs/shared/types/EmailTemplateData";
import { Target } from "@labs/shared/types/Target.js";
import { AnalyzedPlan } from "@labs/shared/types/AnalyzedPlan.js";
import { SolutionMap } from "@labs/shared/types/Solution.js";
import { EmissionsBySource } from "@labs/shared/types/EmissionsBySource.js";
import { toReadableName } from "@labs/shared/util/toReadableName.js";
import { generateSolutionCopyByScope } from "@labs/shared/util/generateSolutionCopyByScope";
import { EmailTheme } from "../types/EmailTheme";
import { getCostScoreColors } from "./getCostScoreColors.js";

const roundAbsolute = (value: number): number => Math.abs(Math.round(value));

export const generateEmailTemplateData = (
  solutionMap: SolutionMap,
  baselineEmissions: EmissionsBySource,
  target: Target,
  analyzedPlan: AnalyzedPlan,
  appBaseUrl: string,
  waterfallChartImageUrl: string,
  forecastLineChartImageUrl: string,
  savingsBySourceChartImageUrl: string,
  emailTheme: EmailTheme,
): EmailTemplateData => {
  const isReduction = analyzedPlan.planAbsoluteImpact < 0;
  const hasGap = analyzedPlan.gap < 0;
  const meetsTarget = analyzedPlan.gap === 0;

  const roundedAbsPlanAbsoluteImpact = roundAbsolute(
    analyzedPlan.planAbsoluteImpact,
  );
  const roundedAbsGap = roundAbsolute(analyzedPlan.gap);
  const roundedAbsBusinessGrowth = roundAbsolute(analyzedPlan.businessGrowth);
  const roundedAbsCleanerGrid = roundAbsolute(analyzedPlan.cleanerGrid);
  const roundedEmissionsRemainingAfterPlan = Math.round(
    analyzedPlan.emissionsRemainingAfterPlan,
  );

  const topSourcesBeforePlan = Object.entries(baselineEmissions)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2)
    .map(([source]) => toReadableName(source));
  const topSourcesAfterPlan = Object.entries(analyzedPlan.footprintAfterPlan)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2)
    .map(([source]) => toReadableName(source));

  const solutionsCount = Object.values(solutionMap).filter(
    (val) => val > 0,
  ).length;

  const meetsExceedsText = (() => {
    if (hasGap) return "falls short of";
    if (meetsTarget) return "meets";
    return "exceeds";
  })();

  const solutionCopyByScope = generateSolutionCopyByScope(solutionMap);

  const simpleRecommendations = analyzedPlan.recommendations.map((r) => ({
    title: r.title,
    description: r.description,
  }));

  const costScoreColors = getCostScoreColors(
    analyzedPlan.costScore,
    emailTheme,
  );

  return {
    roundedAbsoluteValues: {
      planAbsoluteImpact: roundedAbsPlanAbsoluteImpact,
      gap: roundedAbsGap,
      cleanerGrid: roundedAbsCleanerGrid,
    },
    isReduction,
    planOnTrack: analyzedPlan.gap >= 0,
    plainTextFallback:
      "We're sorry, your plan cannot be viewed in a plain text email. Please enable HTML rendering to view your plan.",
    subjectLine: `Your emissions reduction plan achieves a ${roundedAbsPlanAbsoluteImpact}% ${isReduction ? "reduction" : "increase"}`,
    topLevelSummary: `Congratulations! You have taken the first step towards building an emissions reduction plan. Based on your inputs, you could achieve a ${roundedAbsPlanAbsoluteImpact}% ${isReduction ? "reduction" : "increase"} from ${target.baselineYear} emissions. This results in a ${roundedAbsGap}% ${hasGap ? "gap" : "overage"} ${hasGap ? "to" : "beyond"} your reduction target.`,
    emissionsBySourceSummary: `Your top sources of scope 1 + 2 emissions are ${topSourcesBeforePlan[0]} and ${topSourcesBeforePlan[1]}. After your selected solutions are implemented, your top remaining emissions sources will be ${topSourcesAfterPlan[0]} and ${topSourcesAfterPlan[1]}.`,
    businessGrowthSummary: `As your business grows so will your emissions. Our business-as-usual scenario assumes a linear relationship between growth and emissions. Your emissions will ${analyzedPlan.businessGrowth > 0 ? "grow" : "shrink"} by ${roundedAbsBusinessGrowth} units by ${target.targetYear} based on ${target.expectedBusinessGrowth}% annual business growth.`,
    solutionsSummary: `You selected a mix of ${solutionsCount} decarbonization solutions. Implementing these solutions will ${analyzedPlan.planAbsoluteImpact < 0 ? "reduce" : "increase"} your ${target.baselineYear} emissions by ${roundedAbsPlanAbsoluteImpact}%. View these solutions below.`,
    remainingEmissionsSummary: `After implementing the solutions in this plan, your remaining emissions will be ${roundedEmissionsRemainingAfterPlan}% of ${target.baselineYear} emissions, which ${meetsExceedsText} your target to reduce base year emissions by ${target.goal}%.`,
    simpleRecommendations,
    solutionCopyByScope,
    target,
    costScoreColors,
    urls: {
      appBaseUrl,
      waterfallChartImageUrl,
      forecastLineChartImageUrl,
      savingsBySourceChartImageUrl,
    },
  };
};
